import { LOCATION_CHANGE, replace } from 'connected-react-router'
import moment from 'moment'
import axios from 'axios'
import cloneDeep from 'lodash/cloneDeep'
import { Intent } from '@blueprintjs/core'
import { createReducer, TIMESTAMP_FORMAT } from 'src/utils/reducer'
import { buildQueryString } from 'src/utils/queryUtils'
import {
    createErrorToast,
    dispatchErrorToast,
} from 'src/store/modules/utils/toast'
import * as actionTypes from 'src/constants/actionTypes'
import * as urls from 'src/constants/urls'
import actions, {
    initialActionsState,
    BULK_UPDATE_ACTIONS,
    getTopAction,
    getAction,
    PENDING,
} from './actions'
import sessions, {
    initialSessionsState,
} from 'src/store/modules/runbooks/sessions'
import AH from './actions/ActionsHelper'
import { getUser } from 'src/store/modules/users'
import { getTeam } from 'src/store/modules/teams'

import { transformTemplateActions } from './utils/templateUtils'
import exportCsv, {
    transformActivitiesLogData,
    transformCommentLogData,
    transformDecisionsLogData,
    transformIssueLogData,
    transformRunbookData,
} from 'src/store/modules/runbooks/utils/exportCsv'
import { LOGS } from 'src/routes/Runbook/Controls/FilterControls'
import { generateExcelFile } from './utils/generateExcelFile'
import runbookTemplates, {
    initialRunbookTemplatesState,
} from './runbookTemplates'

export const ALL_SOLUTIONS = 0
export const BUSINESS_SOLUTIONS = 1
export const DR_SOLUTIONS_GENERIC = 2
export const DR_SOLUTIONS_RESTART = 3
export const DR_SOLUTIONS_FAILOVER = 4
export const FREE_FORM = 5

export const DEV = 0
export const TEST = 1
export const ACC = 2
export const PROD = 3
export const ARCHIVED = 4

export const MODES = {
    byId: {
        [ALL_SOLUTIONS]: {
            description: 'All active solutions',
            text: 'All Solutions',
        },
        [DR_SOLUTIONS_GENERIC]: {
            description: 'All active DR Solutions',
            text: 'DR Solutions - Generic',
        },
        [DR_SOLUTIONS_RESTART]: {
            description: 'DR Restart Scenario',
            text: 'DR Solutions - Restart',
        },
        [DR_SOLUTIONS_FAILOVER]: {
            description: 'DR Failover Scenario',
            text: 'DR Solutions - Failover',
        },
        [BUSINESS_SOLUTIONS]: {
            description: 'Active business solutions',
            text: 'Business Solutions',
        },
        [FREE_FORM]: {
            description: 'Blank runbook, add solutions manually',
            text: 'Freeform',
        },
    },
    ids: [
        ALL_SOLUTIONS,
        BUSINESS_SOLUTIONS,
        DR_SOLUTIONS_GENERIC,
        DR_SOLUTIONS_RESTART,
        DR_SOLUTIONS_FAILOVER,
        FREE_FORM,
    ],
}

const PROD_CMDB = '1. Production'
const ACC_CMDB = '2. Acceptance'
const TST_CMDB = '3. Test'
const DEV_CMDB = '4. Development'

export const CMDB_ENVS = {
    byId: {
        [PROD_CMDB]: { short: 'Prod' },
        [ACC_CMDB]: { short: 'Acc' },
        [TST_CMDB]: { short: 'Test' },
        [DEV_CMDB]: { short: 'Dev' },
    },
    ids: [PROD_CMDB, ACC_CMDB, TST_CMDB, DEV_CMDB],
}

// Helper function to reset all actions to PENDING state
const resetAllActionsState = (actionsById) => {
    const newActionsById = { ...actionsById }
    for (const uid in newActionsById) {
        const currentAction = newActionsById[uid]
        newActionsById[uid] = {
            ...currentAction,
            status: PENDING,
            startTime: null,
            endTime: null,
        }
    }
    return newActionsById
}

// --> reducers <--
export const initialRunbookState = {
    id: null,
    name: 'Any runbook name',
    organization: null,
    // organization: 'Cegeka',
    orgPk: null,
    // orgPk: 656,
    mode: FREE_FORM,
    parameters: [],
    environments: CMDB_ENVS.ids,
    stage: DEV,
    isEditing: false,
    admins: [],
    adminTeams: [],
    maxDepth: 0,
    activeSession: null,
    actions: initialActionsState,
    sessions: initialSessionsState,
    topAction: null,
    runbookStartTime: null,
    potentialEndTime: null,
    actionLogs: [],
    loadingCounts: false,
    filterQuery: {},
    totalActions: 0,
    pendingActions: 0,
    successActions: 0,
    inProgressActions: 0,
    failedActions: 0,
    customerInfo: null,
    isValid: true,
    invalidMessage: '',
    createdBy: null,
    warnings: [],
    awaitingActionUpdate: [],
    templateId: null,
}

export const genericRunbookActionSingle = (state, action) => ({
    ...state,
    actions: actions(state.actions, action),
    sessions: sessions(state.sessions, action),
})

// This reducer is used to add data to runbook entries. It is called in the exported "runbooks" reducer.
const runbook = createReducer(initialRunbookState, {
    [actionTypes.UPDATE_RUNBOOK_SESSION_ACTIONS]: (state, action) => {
        return {
            ...state,
            awaitingActionUpdate: action.payload.action_uids,
        }
    },
    [actionTypes.UPDATE_RUNBOOK_SESSION_ACTIONS_RECEIVED]: (state, action) => {
        if (state.activeSession === null) {
            // Session is no longer active. Ensure all actions are PENDING, handling race conditions.
            return {
                ...state,
                awaitingActionUpdate: [],
                actions: {
                    ...state.actions,
                    byId: resetAllActionsState(state.actions.byId),
                },
            }
        }
        return {
            ...state,
            awaitingActionUpdate: [],
        }
    },

    [actionTypes.UPDATE_RUNBOOK_SESSION_ACTIONS_ERROR]: (state, action) => {
        if (state.activeSession === null) {
            // Session is no longer active. Ensure all actions are PENDING and times are cleared, handling race conditions.
            return {
                ...state,
                awaitingActionUpdate: [],
                actions: {
                    ...state.actions,
                    byId: resetAllActionsState(state.actions.byId),
                },
            }
        }
        return {
            ...state,
            awaitingActionUpdate: [],
        }
    },

    [actionTypes.UPDATE_RUNBOOK]: (state, action) => {
        if (action.data.runbookStartTime) {
            state.runbookStartTime = action.data.runbookStartTime
            const helper = new AH(state)
            helper.startEstimateRunbook()
        }
        return {
            ...state,
            ...action.data,
        }
    },
    [actionTypes.APPLY_TEMPLATE_ACTIONS]: (state, action) => {
        return {
            ...state,
            actions: {
                ...state.actions,
                byId: action.byId,
                uids: action.uids,
                allUids: action.allUids,
                orphanedUids: [],
                ciToUid: {},
                actionLogs: {},
                topAction: action.firstAction,
                lastUid: action.lastUid,
            },
            topAction: action.firstAction,
            maxDepth: action.maxDepth,
        }
    },
    [actionTypes.START_FORECASTING]: (state, action) => {
        if (action.data.runbookStartTime) {
            state.runbookStartTime = action.data.runbookStartTime
            const helper = new AH(state)
            helper.startEstimateRunbook()
        }
        return {
            ...state,
            ...action.data,
        }
    },
    [actionTypes.ADD_NEW_ACTION]: (state, action) => {
        const helper = new AH(state)
        if (action.options.fromAction && action.options.parent) {
            state.actions.byId[action.options.fromAction].expanded = true
        }
        const newAction = helper.addNewAction(action)
        newAction.visible = true
        // TODO Maybe we're adding a new top action here?
        return {
            ...state,
            maxDepth: helper.calculateMaxDepth(),
            topAction: helper.topAction,
            actions: {
                ...state.actions,
                byId: {
                    ...state.actions.byId,
                    [newAction.uid]: newAction,
                },
                uids: helper.createActionsList(),
                lastUid: helper.lastUid,
                ciToUid: { ...helper.ciToUid },
                topAction: helper.topAction,
            },
        }
    },
    [actionTypes.ADD_NEW_DEVICE]: (state, action) => {
        const helper = new AH(state)
        const { device } = action
        const newAction = helper.addNewAction({
            data: helper.mapDeviceToAction(device),
            options: action.options,
            device,
        })
        const ciToUid = { ...helper.ciToUid }

        // TODO: perform this update in the actionshelper
        ciToUid[action.device.id].details = {
            ...device,
        }

        newAction.visible = true
        return {
            ...state,
            maxDepth: helper.calculateMaxDepth(),
            actions: {
                ...state.actions,
                byId: { ...helper.actionsMap },
                uids: helper.createActionsList(),
                lastUid: helper.lastUid,
                ciToUid,
            },
        }
    },
    [actionTypes.ADD_SOLUTION_WITH_CHILDREN]: (state, action) => {
        const helper = new AH(state)
        helper.addSolutionWithChildren(action)
        return {
            ...state,
            maxDepth: helper.calculateMaxDepth(),
            actions: {
                ...state.actions,
                byId: { ...helper.actionsMap },
                uids: helper.createActionsList(),
                lastUid: helper.lastUid,
                ciToUid: { ...helper.ciToUid },
            },
        }
    },
    [actionTypes.GET_ACTION_LOGS_SUCCESS]: genericRunbookActionSingle,
    [actionTypes.GET_ALL_ACTION_LOGS_SUCCESS]: (state, action) => {
        return {
            ...state,
            actionLogs: action.payload.data.results.map((log) => ({
                createdBy: log.created_by,
                message: log.message,
                logType: log.log_type,
                id: log.id,
                createdAt: moment(log.date_created).format(TIMESTAMP_FORMAT),
                uid: log.action_uid,
            })),
            page: action.payload.data._page.page,
            totalElements: action.payload.data._page.total_elements,
            totalPages: action.payload.data._page.total_pages,
            perPage: action.payload.data._page.per_page,
        }
    },
    [actionTypes.GET_ALL_DECISION_LOGS_SUCCESS]: (state, action) => {
        return {
            ...state,
            decisionLogs: action.payload.data.results.map((log) => ({
                id: log.id,
                decision: log.decision,
                createdAt: moment(log.logged).format(TIMESTAMP_FORMAT),
                owner: log.owner,
                reasoning: log.reasoning,
                sessionId: log.session,
                actionUid: log.action_uid,
            })),
            page: action.payload.data._page.page,
            totalElements: action.payload.data._page.total_elements,
            totalPages: action.payload.data._page.total_pages,
            perPage: action.payload.data._page.per_page,
        }
    },
    [actionTypes.GET_ALL_ISSUE_LOGS_SUCCESS]: (state, action) => {
        return {
            ...state,
            issueLogs: action.payload.data.results.map((log) => ({
                id: log.id,
                due: moment(log.due).toISOString(),
                description: log.description,
                solution: log.solution,
                createdAt: moment(log.registered).format(TIMESTAMP_FORMAT),
                owner: log.owner,
                priority: log.priority,
                severity: log.severity,
                status: log.status,
                actionUid: log.action_uid,
            })),
            page: action.payload.data._page.page,
            totalElements: action.payload.data._page.total_elements,
            totalPages: action.payload.data._page.total_pages,
            perPage: action.payload.data._page.per_page,
        }
    },
    [actionTypes.GET_ALL_ACTIVITY_LOGS_SUCCESS]: (state, action) => {
        return {
            ...state,
            activityLogs: action.payload.data.results.map((log) => ({
                id: log.id,
                source: log.source,
                date: moment(log.date).toISOString(),
                description: log.description,
                owner: log.owner,
                company: log.company,
                priority: log.priority,
                due: moment(log.due).toISOString(),
                comments: log.comments,
                closed: log.closed,
                status: log.status,
                actionUid: log.action_uid,
                sessionId: log.session,
                createdAt: moment().format(TIMESTAMP_FORMAT),
            })),
            page: action.payload.data._page.page,
            totalElements: action.payload.data._page.total_elements,
            totalPages: action.payload.data._page.total_pages,
            perPage: action.payload.data._page.per_page,
        }
    },
    [actionTypes.UPDATE_ACTION]: (state, action) => {
        const updatedState = genericRunbookActionSingle(state, action)
        const helper = new AH(updatedState)
        helper.startEstimateRunbook()

        const urlParams = new URLSearchParams(window.location.search)

        if (urlParams.get('RBK490') === 'false') {
            console.warn('Disabled changes related to RBK490')
        } else {
            if (state.activeSession) {
                // Skip runbook validation
                // The runbook validation checks dependencies and circular trees
                // They are time consuming in large runbooks and cannot be influenced by the active session
                return {
                    ...updatedState,
                }
            }
        }

        const { success, message, warnings } = helper.validateRunbook()
        if (success) {
            return {
                ...updatedState,
                isValid: true,
                invalidMessage: '',
                warnings: warnings,
            }
        }
        return {
            ...state,
            isValid: false,
            invalidMessage: message,
            warnings,
        }
    },
    [actionTypes.EXPAND_ACTION]: genericRunbookActionSingle,
    [actionTypes.REFRESH_SOLUTION_SUCCESS]: (state, action) => {
        const helper = new AH(state)
        helper.cmdbData = action.payload

        // Use the cmdb customer solutions map to refresh the runbook action(s)
        helper.refreshSolutionsFromCmdb(
            action.actionId,
            action.payload,
            action.params
        )

        return {
            ...state,
            maxDepth: helper.calculateMaxDepth(),
            actions: {
                ...state.actions,
                byId: { ...helper.actionsMap },
                uids: helper.createActionsList(),
                lastUid: helper.lastUid,
                ciToUid: { ...helper.ciToUid },
            },
        }
    },
    [BULK_UPDATE_ACTIONS]: genericRunbookActionSingle,
    [actionTypes.MOVE_ACTION]: (state, action) => {
        // use a backup of the state so if the move is invalid,
        // we don't break the state
        const backup = cloneDeep(state)
        const helper = new AH(backup)
        // we check the move is valid or not in helper.moveAction
        // if it's invalid, we return the previous state that didn't get mutated
        const { success, message, warnings } = helper.moveAction({
            uid: action.actionId,
            toAction: action.to,
            parent: action.parent,
        })
        if (!success) {
            return {
                ...state,
                isValid: false,
                invalidMessage: message,
                warnings,
            }
        }
        const uids = helper.createActionsList()
        const orphanedUids = helper.getOrphanedUids(uids)
        return {
            ...backup,
            maxDepth: helper.calculateMaxDepth(),
            topAction: helper.calculateTopActionUid(),
            actions: {
                ...backup.actions,
                uids,
                orphanedUids,
                byId: { ...helper.actionsMap },
                ciToUid: { ...helper.ciToUid },
            },
            isValid: true,
            invalidMessage: '',
            warnings,
        }
    },
    [actionTypes.MOVE_ACTIONS]: (state, action) => {
        // use a backup of the state so if the moves are invalid,
        // we don't break the state
        const backup = cloneDeep(state)
        const helper = new AH(state)
        // we check the move is valid or not in helper.moveAction
        // if it's invalid, we throw an error without updating the state
        const { to, parent, firstChild, sortAlphabetically } = action
        const { success, message, warnings } = helper.moveActions(action.ids, {
            to,
            parent,
            firstChild,
            sortAlphabetically,
        })
        if (!success) {
            return {
                ...backup,
                isValid: false,
                invalidMessage: message,
                warnings,
            }
        }
        const uids = helper.createActionsList()
        const orphanedUids = helper.getOrphanedUids(uids)
        return {
            ...state,
            maxDepth: helper.calculateMaxDepth(),
            topAction: helper.calculateTopActionUid(),
            actions: {
                ...state.actions,
                uids,
                orphanedUids,
                byId: { ...helper.actionsMap },
                ciToUid: { ...helper.ciToUid },
            },
            warnings,
        }
    },
    [actionTypes.FOCUS_ACTION]: genericRunbookActionSingle,
    [actionTypes.TOGGLE_FREEZE_OWNER]: genericRunbookActionSingle,
    [actionTypes.MARK_INFO_ONLY]: (state, action) => {
        const helper = new AH(state)
        helper.markActionInfoOnly(action.actionId)
        return {
            ...state,
            actions: {
                ...state.actions,
                byId: { ...helper.actionsMap },
            },
        }
    },
    [actionTypes.DELETE_ACTION]: (state, action) => {
        const helper = new AH(state)
        const removedUids = helper.deleteAction(action.id, action.withChildren)
        for (let uid in helper.actionsMap) {
            helper.actionsMap[uid].parents = helper.actionsMap[
                uid
            ].parents.filter((u) => removedUids.indexOf(u) === -1)
        }

        if (
            removedUids.includes(helper.topAction) &&
            Object.keys(helper.actionsMap).length === 0
        ) {
            helper.topAction = null
            helper.lastUid = 1
        }

        helper.startEstimateRunbook()
        const uids = helper.createActionsList()
        const orphanedUids = helper.getOrphanedUids(uids)

        return {
            ...state,
            maxDepth: helper.calculateMaxDepth(),
            topAction: helper.calculateTopActionUid(),
            actions: {
                ...state.actions,
                topAction: helper.calculateTopActionUid(),
                uids,
                orphanedUids,
                byId: { ...helper.actionsMap },
                // uids: state.actions.uids.filter(id => removedUids.indexOf(id) === -1),
                lastUid: helper.lastUid,
                ciToUid: { ...helper.ciToUid },
            },
        }
    },
    [actionTypes.DELETE_MARKED_ACTIONS]: (state) => {
        const helper = new AH(state)
        const removedUids = helper.deleteMarkedActions()
        helper.startEstimateRunbook()
        return {
            ...state,
            actions: {
                ...state.actions,
                maxDepth: helper.calculateMaxDepth(),
                byId: { ...helper.actionsMap },
                uids: state.actions.uids.filter(
                    (id) => removedUids.indexOf(id) === -1
                ),
                lastUid: state.actions.lastUid,
                ciToUid: { ...helper.ciToUid },
            },
        }
    },
    [actionTypes.FILTER_RUNBOOK_ACTIONS]: (state, action) => {
        const helper = new AH(state)
        return {
            ...state,
            filterQuery: action.query,
            actions: {
                ...state.actions,
                byId: { ...helper.applyUserFilters(action.query) },
            },
        }
    },
    [actionTypes.GET_CUSTOMER_SOLUTIONS_RUNBOOK_SUCCESS]: (state, action) => {
        const helper = new AH(state)
        const data = helper.processCmdbData(action.payload.data.results)
        return {
            ...state,
            maxDepth: helper.calculateMaxDepth(),
            actions: {
                ...state.actions,
                ...data,
                actionLogs: {},
            },
        }
    },
    [actionTypes.SET_DEPTH_LEVEL]: genericRunbookActionSingle,
    [actionTypes.GET_RUNBOOKS_SUCCESS]: (state, action) => {
        return {
            ...initialRunbookState,
            ...state,
            createdBy: state.createdBy && state.createdBy.id,
            admins: state.admins.map((user) => user.id),
            adminTeams: state.adminTeams.map((team) => team.id),
            activeSession: state.activeSession && state.activeSession.id,
            actions: {
                ...actions(state.actions, action),
                ciToUid: state.ciToUid,
            },
        }
    },
    [actionTypes.GET_RUNBOOK]: (state) => {
        return {
            ...state,
        }
    },
    [actionTypes.RESET_CREATE_RUNBOOK]: (state) => {
        return {
            ...state,
            ...initialRunbookState,
            actions: {
                byId: {},
                uids: [],
                orphanedUids: [],
                lastUid: 1,
                ciToUid: {},
                actionLogs: {},
                topAction: null,
                loading: false,
            },
        }
    },
    [actionTypes.GET_RUNBOOK_RECEIVED]: (state, action, dispatch) => {
        const runbook = {
            ...initialRunbookState,
            ...state,
            ...action.data.runbook,
            createdBy: action.data.runbook.createdBy.id,
            admins: action.data.runbook.admins.map((user) => user.id),
            adminTeams: action.data.runbook.adminTeams.map((team) => team.id),
            activeSession:
                action.data.runbook.activeSession &&
                action.data.runbook.activeSession.id,
            runbookStartTime:
                (action.data.runbook.activeSession &&
                    new Date(action.data.runbook.activeSession.started)) ||
                state.runbookStartTime,
            actions: actions(
                {
                    ...state.actions,
                    ciToUid: action.data.runbook.ciToUid,
                    topAction: action.data.runbook.topAction,
                },
                action
            ),
        }
        const helper = new AH(runbook)

        const validation = helper.validateActionLinks()

        helper.startEstimateRunbook()
        runbook.actions.uids = helper.createActionsList()
        runbook.actions.validation = validation
        runbook.actions.orphanedUids = helper.getOrphanedUids(
            runbook.actions.uids
        )
        return runbook
    },
    [actionTypes.EXPORT_RUNBOOK]: (state) => {
        // Map the dataSet the way we want it to easily export the fields we want in the format we want
        const results = transformRunbookData(state)
        const filename = `export_runbook_${state.id}_${state.name}.csv`
        exportCsv(results, filename)
        return state
    },
    [actionTypes.GENERATE_RUNBOOK_CSV_SUCCESS]: (state, action) => {
        const acts = actions(
            {
                ...state.actions,
                ciToUid: action.payload.data.ciToUid,
                topAction: 1,
            },
            {
                type: actionTypes.GET_RUNBOOK_RECEIVED,
                data: { actions: action.payload.data.results },
            }
        )
        const helper = new AH({ ...state, actions: acts })
        return {
            ...state,
            actions: acts,
            maxDepth: helper.calculateMaxDepth(),
        }
    },
    [actionTypes.EXPORT_HISTORY_LOGS]: (state, action) => {
        const results = action.data.map((item) => {
            return {
                'Runbook Id': action.runbookId,
                'Runbook name': state.name,
                'Customer info': item.action?.customer ?? '',
                Id: item.id,
                Uid: item.uid ?? '',
                'Action name': item.action?.name ?? '',
                Message: item.message,
                'Date created': item.createdAt,
                'First name': item.createdBy.first_name,
                'Last name': item.createdBy.last_name,
                Username: item.createdBy.username,
                Email: item.createdBy.email,
            }
        })
        const filename = `export_history_logs_${state.id}_${state.name}.csv`
        exportCsv(results, filename)
        return state
    },
    [actionTypes.EXPORT_COMMENT_LOGS]: (state, action) => {
        const results = transformCommentLogData(state, action)
        const filename = `export_comment_logs_${state.id}_${state.name}.csv`
        exportCsv(results, filename)
        return state
    },
    [actionTypes.EXPORT_ISSUE_LOGS]: (state, action) => {
        const results = transformIssueLogData(state, action)
        const filename = `export_issue_logs_${state.id}_${state.name}.csv`
        exportCsv(results, filename)
        return state
    },
    [actionTypes.EXPORT_DECISION_LOGS]: (state, action) => {
        const results = transformDecisionsLogData(state, action)
        const filename = `export_decision_logs_${state.id}_${state.name}.csv`
        exportCsv(results, filename)
        return state
    },
    [actionTypes.EXPORT_ACTIVITIES_LOGS]: (state, action) => {
        const results = transformActivitiesLogData(state, action)
        const filename = `export_activities_logs_${state.id}_${state.name}.csv`
        exportCsv(results, filename)
        return state
    },
    [actionTypes.SET_ACTIVE_RUNBOOK]: (state, action) => {
        return {
            ...state,
            isEditing: action.runbookId === null,
        }
    },
    [actionTypes.EDIT_RUNBOOK_ACTIONS_RECEIVED]: (state, action) => {
        const { byId } = state.actions
        const { newActions } = action.payload.data
        for (let uid in newActions) {
            byId[uid].id = newActions[uid]
        }
        return {
            ...state,
            isEditing: false,
            actions: {
                ...state.actions,
                byId: { ...byId },
            },
        }
    },
    [actionTypes.SAVE_SUPPLIER_SUCCESS]: genericRunbookActionSingle,
    [actionTypes.START_RUNBOOK_SESSION]: (state, action) => {
        return {
            ...state,
            sessionLoading: true,
        }
    },
    [actionTypes.START_RUNBOOK_SESSION_SUCCESS]: (state, action) => {
        if (action.data.session) {
            state.runbookStartTime = new Date(action.data.session.started)
            const helper = new AH(state)
            helper.startEstimateRunbook()
        }

        return {
            ...state,
            sessionLoading: false,
            activeSession: action.data.session.id,
            runbookStartTime: new Date(action.data.session.started),
        }
    },
    [actionTypes.END_RUNBOOK_SESSION]: (state, action) => {
        return {
            ...state,
            sessionLoading: true,
        }
    },
    [actionTypes.END_RUNBOOK_SESSION_SUCCESS]: (state, action) => {
        // When a session ends, reset all action statuses in the UI to PENDING, handling race conditions.
        return {
            ...state,
            sessionLoading: false,
            activeSession: null,
            runbookStartTime: null,
            actions: {
                ...state.actions,
                byId: resetAllActionsState(state.actions.byId),
            },
        }
    },
    [actionTypes.RUNBOOK_ACTIONS_LOADING]: genericRunbookActionSingle,
})

export const genericRunbookAction = (state, action) => {
    if (action.runbookId !== undefined) {
        return {
            ...state,
            byId: {
                ...state.byId,
                [action.runbookId]: runbook(
                    state.byId[action.runbookId] || initialRunbookState,
                    action
                ),
            },
            values: [...state.values],
        }
    }
    return state
}

export const ALL_ACCESS = 'all'
export const ADMIN_ACESS = 'admin'
export const OWNER_ACCESS = 'owner'
export const READ_ONLY_ACCESS = 'read-only'

export const ACCESS_OPTIONS = [
    {
        value: ALL_ACCESS,
        label: 'All',
        selected: true,
    },
    {
        value: ADMIN_ACESS,
        label: 'Admin',
        selected: false,
    },
    {
        value: OWNER_ACCESS,
        label: 'Owner',
        selected: false,
    },
    {
        value: READ_ONLY_ACCESS,
        label: 'Read-Only',
        selected: false,
    },
]

export const STAGE_OPTIONS = [
    {
        label: 'All',
        selected: true,
    },
    {
        value: DEV,
        label: 'In Development',
        selected: false,
    },
    {
        value: TEST,
        label: 'Ready for Test',
        selected: false,
    },
    {
        value: ACC,
        label: 'Ready for Use',
        selected: false,
    },
    // {
    //     value: PROD,
    //     label: 'Ready for Prod',
    //     selected: true,
    // },
    {
        value: ARCHIVED,
        label: 'Archived',
        selected: false,
    },
]

export const allAccess = (state) => state.runbooks.access[0].selected === true

// This is the exported reducer which will get to the actions first. It calls the local "runbook" reducer for appending the data to the runbooks.
const runbooks = createReducer(
    {
        byId: {
            null: initialRunbookState,
        },
        values: [null],
        templates: initialRunbookTemplatesState,
        loading: true,
        access: ACCESS_OPTIONS,
        stage: STAGE_OPTIONS,
        customerFilter: [],
        controls: {},
        page: 1,
        totalElements: null,
        perPage: 10,
        totalPages: null,
    },
    {
        [actionTypes.UPDATE_RUNBOOK]: (state, action) => ({
            ...state,
            byId: {
                ...state.byId,
                [action.id]: runbook(state.byId[action.id], action),
            },
            values: [...state.values],
        }),
        [actionTypes.APPLY_TEMPLATE_ACTIONS]: (state, action) => ({
            ...state,
            byId: {
                ...state.byId,
                [action.id]: runbook(state.byId[action.id], action),
            },
            values: [...state.values],
        }),
        [actionTypes.START_FORECASTING]: (state, action) => ({
            ...state,
            byId: {
                ...state.byId,
                [action.id]: runbook(state.byId[action.id], action),
            },
            values: [...state.values],
        }),
        [actionTypes.ADD_NEW_ACTION]: genericRunbookAction,
        [actionTypes.ADD_NEW_DEVICE]: genericRunbookAction,
        [actionTypes.ADD_NEW_DECISION]: genericRunbookAction,
        [actionTypes.ADD_NEW_ISSUE]: genericRunbookAction,
        [actionTypes.ADD_SOLUTION_WITH_CHILDREN]: genericRunbookAction,
        [actionTypes.UPDATE_ACTION]: genericRunbookAction,
        [actionTypes.EXPAND_ACTION]: genericRunbookAction,
        [actionTypes.EXPORT_RUNBOOK]: genericRunbookAction,
        [actionTypes.EXPORT_HISTORY_LOGS]: genericRunbookAction,
        [actionTypes.EXPORT_COMMENT_LOGS]: genericRunbookAction,
        [actionTypes.EXPORT_ISSUE_LOGS]: genericRunbookAction,
        [actionTypes.EXPORT_DECISION_LOGS]: genericRunbookAction,
        [actionTypes.EXPORT_ACTIVITIES_LOGS]: genericRunbookAction,
        [actionTypes.EXPORT_RUNBOOK_LEGACY]: genericRunbookAction,
        [actionTypes.REFRESH_SOLUTION_SUCCESS]: genericRunbookAction,
        [BULK_UPDATE_ACTIONS]: genericRunbookAction,
        [actionTypes.MOVE_ACTION]: genericRunbookAction,
        [actionTypes.MOVE_ACTIONS]: genericRunbookAction,
        [actionTypes.FOCUS_ACTION]: genericRunbookAction,
        [actionTypes.MARK_INFO_ONLY]: genericRunbookAction,
        [actionTypes.TOGGLE_FREEZE_OWNER]: genericRunbookAction,
        [actionTypes.GET_RUNBOOK]: genericRunbookAction,
        [actionTypes.RESET_CREATE_RUNBOOK]: genericRunbookAction,
        [actionTypes.GET_RUNBOOK_RECEIVED]: genericRunbookAction,
        [actionTypes.EDIT_RUNBOOK_ACTIONS_RECEIVED]: genericRunbookAction,
        [actionTypes.FILTER_RUNBOOK_ACTIONS]: genericRunbookAction,
        [actionTypes.START_RUNBOOK_SESSION]: genericRunbookAction,
        [actionTypes.START_RUNBOOK_SESSION_SUCCESS]: genericRunbookAction,
        [actionTypes.END_RUNBOOK_SESSION]: genericRunbookAction,
        [actionTypes.END_RUNBOOK_SESSION_SUCCESS]: genericRunbookAction,
        [actionTypes.RUNBOOK_ACTIONS_LOADING]: genericRunbookAction,
        [actionTypes.DELETE_MARKED_ACTIONS]: genericRunbookAction,
        [actionTypes.EXPORT_LIST_OF_RUNBOOKS_SUCCESS]: (state, action) => {
            const runbooks = action.payload.data.results
            const results = runbooks.map((runbook) => {
                return {
                    'Runbook ID': runbook.id,
                    'Related organization': runbook.organization,
                    'Runbook type': MODES.byId[runbook.mode].text,
                    'Runbook name': runbook.name,
                    Parameters:
                        runbook.mode === DR_SOLUTIONS_RESTART
                            ? runbook.parameters.join(', ')
                            : runbook.mode === DR_SOLUTIONS_FAILOVER
                            ? `From: ${runbook.parameters[0]} To: ${runbook.parameters[1]}`
                            : '',
                    'Modification time': moment(runbook.lastModified).format(
                        TIMESTAMP_FORMAT
                    ),
                    'Runbook owner': runbook.createdBy.fullName,
                    'Runbook status': runbook.activeSession
                        ? 'In progress'
                        : 'Stopped',
                    'Runbook lifecycle': STAGE_OPTIONS[runbook.stage]?.label,
                    'Has filters': runbook.hasFilters ? 'Yes' : 'No',
                }
            })
            exportCsv(results, 'export_of_runbooks_list.csv')
            return {
                ...state,
                loading: false,
            }
        },
        [actionTypes.GET_RUNBOOKS]: (state) => ({
            ...state,
            loading: true,
        }),
        [actionTypes.GET_RUNBOOKS_SUCCESS]: (state, action) => {
            return {
                ...state,
                loading: false,
                page: action.payload.data._page.page,
                totalElements: action.payload.data._page.total_elements,
                totalPages: action.payload.data._page.total_pages,
                perPage: action.payload.data._page.per_page,
                byId: {
                    // ...state.byId,
                    ...action.payload.data.results.reduce(
                        (acc, value) => ({
                            ...acc,
                            [value.id]: runbook(value, action),
                        }),
                        { ...state.byId }
                    ),
                },
                values: [
                    // ...state.values,
                    ...action.payload.data.results.map((v) => v.id),
                ].filter((value, index, self) => self.indexOf(value) === index),
            }
        },
        [actionTypes.GET_RUNBOOK_TEMPLATES]: (state, action) => ({
            ...state,
            templates: runbookTemplates(state.templates, action),
        }),
        [actionTypes.GET_RUNBOOK_TEMPLATES_SUCCESS]: (state, action) => ({
            ...state,
            templates: runbookTemplates(state.templates, action),
        }),
        [actionTypes.CHANGE_RUNBOOK_TEMPLATES_PAGE]: (state, action) => ({
            ...state,
            templates: runbookTemplates(state.templates, action),
        }),
        [actionTypes.GET_RUNBOOK_TEMPLATES_ERROR]: (state, action) => ({
            ...state,
            templates: runbookTemplates(state.templates, action),
        }),
        [actionTypes.GET_RUNBOOK_TEMPLATE]: (state, action) => ({
            ...state,
            templates: runbookTemplates(state.templates, action),
        }),
        [actionTypes.GET_RUNBOOK_TEMPLATE_SUCCESS]: (state, action) => ({
            ...state,
            templates: runbookTemplates(state.templates, action),
        }),
        [actionTypes.GET_RUNBOOK_TEMPLATE_ERROR]: (state, action) => ({
            ...state,
            templates: runbookTemplates(state.templates, action),
        }),
        [actionTypes.GET_RUNBOOK_TEMPLATE_ACTIONS]: (state, action) => ({
            ...state,
            templates: runbookTemplates(state.templates, action),
        }),
        [actionTypes.GET_RUNBOOK_TEMPLATE_ACTIONS_SUCCESS]: (
            state,
            action
        ) => ({
            ...state,
            templates: runbookTemplates(state.templates, action),
        }),
        [actionTypes.GET_RUNBOOK_TEMPLATE_ACTIONS_ERROR]: (state, action) => ({
            ...state,
            templates: runbookTemplates(state.templates, action),
        }),
        [actionTypes.SAVE_RUNBOOK_TEMPLATE_ACTIONS]: (state, action) => ({
            ...state,
            templates: runbookTemplates(state.templates, action),
        }),
        [actionTypes.SAVE_RUNBOOK_TEMPLATE_ACTIONS_SUCCESS]: (
            state,
            action
        ) => ({
            ...state,
            templates: runbookTemplates(state.templates, action),
        }),
        [actionTypes.SAVE_RUNBOOK_TEMPLATE_ACTIONS_ERROR]: (state, action) => ({
            ...state,
            templates: runbookTemplates(state.templates, action),
        }),
        [actionTypes.DELETE_RUNBOOK_TEMPLATE]: (state, action) => ({
            ...state,
            templates: runbookTemplates(state.templates, action),
        }),
        [actionTypes.DELETE_RUNBOOK_TEMPLATE_RECEIVED]: (state, action) => ({
            ...state,
            templates: runbookTemplates(state.templates, action),
        }),
        [actionTypes.DELETE_RUNBOOK_TEMPLATE_ERROR]: (state, action) => ({
            ...state,
            templates: runbookTemplates(state.templates, action),
        }),
        [actionTypes.CREATE_RUNBOOK_TEMPLATE]: (state, action) => ({
            ...state,
            templates: runbookTemplates(state.templates, action),
        }),
        [actionTypes.CREATE_RUNBOOK_TEMPLATE_SUCCESS]: (state, action) => ({
            ...state,
            templates: runbookTemplates(state.templates, action),
        }),
        [actionTypes.CREATE_RUNBOOK_TEMPLATE_ERROR]: (state, action) => ({
            ...state,
            templates: runbookTemplates(state.templates, action),
        }),
        [actionTypes.UPDATE_RUNBOOK_TEMPLATE]: (state, action) => ({
            ...state,
            templates: runbookTemplates(state.templates, action),
        }),
        [actionTypes.UPDATE_RUNBOOK_TEMPLATE_SUCCESS]: (state, action) => ({
            ...state,
            templates: runbookTemplates(state.templates, action),
        }),
        [actionTypes.UPDATE_RUNBOOK_TEMPLATE_ERROR]: (state, action) => ({
            ...state,
            templates: runbookTemplates(state.templates, action),
        }),
        [actionTypes.CHANGE_RUNBOOKS_PAGE]: (state, action) => {
            return {
                ...state,
                page: action.page,
            }
        },
        [actionTypes.DELETE_RUNBOOK_RECEIVED]: (state, action) => {
            const { byId, values } = state
            delete byId[action.runbookId]
            return {
                ...state,
                byId,
                values: values.filter((id) => id !== action.runbookId),
            }
        },
        [actionTypes.DELETE_ACTION]: genericRunbookAction,
        [actionTypes.SET_ACTIVE_RUNBOOK]: genericRunbookAction,
        [actionTypes.GET_CUSTOMER_SOLUTIONS_RUNBOOK_SUCCESS]: genericRunbookAction,
        [actionTypes.GET_ACTION_LOGS_SUCCESS]: genericRunbookAction,
        [actionTypes.GET_ALL_ACTION_LOGS_SUCCESS]: genericRunbookAction,
        [actionTypes.GET_ALL_DECISION_LOGS_SUCCESS]: genericRunbookAction,
        [actionTypes.GET_ALL_ISSUE_LOGS_SUCCESS]: genericRunbookAction,
        [actionTypes.GET_ALL_ACTIVITY_LOGS_SUCCESS]: genericRunbookAction,
        [actionTypes.TOGGLE_RUNBOOKS_ACCESS_TYPE]: (state, action) => {
            return {
                ...state,
                access: state.access.map((accessType) => ({
                    ...accessType,
                    selected: action.payload.access === accessType.value,
                })),
            }
        },
        [actionTypes.TOGGLE_RUNBOOKS_ENVIRONMENT]: (state, action) => {
            return {
                ...state,
                stage: state.stage.map((eType) =>
                    eType.value === action.payload.env
                        ? { ...eType, selected: true }
                        : { ...eType, selected: false }
                ),
            }
        },
        [actionTypes.SET_CUSTOMER_FILTER]: (state, action) => {
            return {
                ...state,
                customerFilter: action.payload.customers,
            }
        },
        [actionTypes.SAVE_SUPPLIER_SUCCESS]: genericRunbookAction,
        [actionTypes.SET_DEPTH_LEVEL]: genericRunbookAction,
        [actionTypes.GENERATE_RUNBOOK_CSV_SUCCESS]: genericRunbookAction,
        [LOCATION_CHANGE]: (state, action) => {
            if (action.payload.location.pathname === window.location.pathname) {
                return state
            }
            return {
                ...state,
                byId: {
                    null: initialRunbookState,
                },
                values: [null],
                loading: true,
            }
        },
        [actionTypes.UPDATE_RUNBOOK_SESSION_ACTIONS]: (state, action) => {
            return {
                ...state,
                byId: {
                    ...state.byId,
                    [action.runbookId]: runbook(
                        state.byId[action.runbookId] || initialRunbookState,
                        action
                    ),
                },
            }
        },
        [actionTypes.UPDATE_RUNBOOK_SESSION_ACTIONS_RECEIVED]: (
            state,
            action
        ) => {
            const { runbookId } = action
            return {
                ...state,
                byId: {
                    ...state.byId,
                    [runbookId]: runbook(
                        state.byId[runbookId] || initialRunbookState,
                        action
                    ),
                },
            }
        },

        [actionTypes.UPDATE_RUNBOOK_SESSION_ACTIONS_ERROR]: (state, action) => {
            const { runbookId } = action
            return {
                ...state,
                byId: {
                    ...state.byId,
                    [runbookId]: runbook(
                        state.byId[runbookId] || initialRunbookState,
                        action // Pass the action to the specific runbook
                    ),
                },
            }
        },
    }
)

// --> selectors <--

const isOwner = (user, runbook) => runbook.createdBy === user?.id

const isAdmin = (user, runbook) =>
    runbook.admins.filter((adminId) => adminId === user?.id).length >= 1 ||
    runbook.adminTeams.filter((value) => user?.teams.indexOf(value) !== -1)
        .length >= 1

export const getRunbook = (state, runbookId) => {
    const runbook = state.runbooks.byId[runbookId]
    if (!runbook) return undefined
    return {
        ...runbook,
        createdBy: getUser(state, runbook.createdBy),
        admins: runbook.admins.map((userId) => getUser(state, userId)),
        adminTeams: runbook.adminTeams.map((teamId) => getTeam(state, teamId)),
        isOwner: isOwner(state.app.user, runbook),
        isAdmin: isAdmin(state.app.user, runbook),
    }
}

export const getActiveRunbook = (state) =>
    getRunbook(state, state.ui.runbook.activeRunbook)

export const getRunbooks = (state) =>
    state.runbooks.values.map((runbookId) => getRunbook(state, runbookId))

/**
 * Check if an action should be considered hidden for execution purposes
 * An action is hidden if:
 * 1. It's filtered by user (UI filters)
 * 2. It's filtered by action (CMDB/DR filters)
 * 3. It's an empty DR Solution with no children
 * @param {Object} action - The action to check
 * @returns {boolean} - True if the action should be hidden
 */
export const shouldActionBeHidden = (action) => {
    if (!action) return false

    // Check existing filter flags
    if (action.filteredByUser || action.filteredByAction) {
        return true
    }

    // Additional check: Empty DR Solutions should be hidden
    if (
        action.deviceClass === 'DR Solution' &&
        (!action.treeChildren || action.treeChildren.length === 0)
    ) {
        return true
    }

    return false
}

/**
 * Collect hidden action UIDs from actionsById object
 * This is a helper that doesn't need the full Redux state
 * @param {Object} actionsById - Actions map from runbook.actions.byId
 * @returns {Array} Array of hidden action UIDs
 */
export const collectHiddenActionUids = (actionsById) => {
    if (!actionsById) return []

    const hiddenUids = []

    for (const uid in actionsById) {
        const act = actionsById[uid]
        if (!act) continue

        // Check if filtered by user or action
        const isFiltered = act.filteredByUser || act.filteredByAction

        // Also check if it's an empty DR Solution
        const isEmptyDRSolution =
            act.deviceClass === 'DR Solution' &&
            (!act.treeChildren || act.treeChildren.length === 0)

        if (isFiltered || isEmptyDRSolution) {
            hiddenUids.push(parseInt(uid))
        }
    }

    return hiddenUids
}

/**
 * Collect all hidden action UIDs from a runbook (from Redux state)
 * @param {Object} state - Redux state
 * @param {Number} runbookId - Runbook ID
 * @returns {Array} Array of hidden action UIDs
 */
export const getHiddenActionUids = (state, runbookId) => {
    const runbook = state.runbooks.byId[runbookId]
    if (!runbook || !runbook.actions || !runbook.actions.byId) {
        return []
    }

    return collectHiddenActionUids(runbook.actions.byId)
}

const formatAction = (action) => ({
    name: action.name,
    device: action.device,
    parents: action.parents,
    children: action.children,
    tree_children: action.treeChildren,
    uid: action.uid,
    tree_parent: action.treeParent,
    prev_sibling: action.prevSibling,
    next_sibling: action.nextSibling,
    owner_user_id: action.ownerUserId,
    owner_team_id: action.ownerTeamId,
    owner_supplier_id: action.ownerSupplierId,
    cmdb_owner_team_id: action.cmdbOwnerTeamId,
    freeze_owner_team: action.freezeOwnerTeam,
    environment: action.environment,
    device_class: action.deviceClass,
    reverse: action.reverse,
    auto_succeed: action.autoSucceed,
    description: action.description,
    monitored: action.monitored,
    custom_url: action.customUrl,
    color: action.color,
    estimated_duration: action.estimatedDuration,
    customer_id: action.customerId,
    info_only: action.infoOnly,
    depth: action.depth,
    is_av_zone: action.isAvZone,
    positionX: action.positionX,
    positionY: action.positionY,
    doNotStartBeforeTime: action.doNotStartBeforeTime,
    filters: action.filters,
})

// --> actions <--
export const updateRunbook = (id, data) => {
    return {
        type: actionTypes.UPDATE_RUNBOOK,
        id,
        data,
    }
}

const dispatchWarningNotifications = (warnings, dispatch) => {
    if (warnings.length) {
        dispatch({
            type: actionTypes.TOAST_SHOW,
            message: {
                content: warnings.join('\n'),
                intent: Intent.WARNING,
                icon: 'error',
            },
        })
    }
}

const validateNotifyUserAndDispatchAction = (
    runbook,
    dispatch,
    getState,
    dispatchAction
) => {
    // check if runbook is valid
    const ah = new AH(runbook)
    const { success, message, warnings } = ah.validateRunbook()
    if (success) {
        dispatchWarningNotifications(warnings, dispatch)
        dispatchAction(runbook, dispatch, getState)
    } else {
        dispatch({
            type: actionTypes.TOAST_SHOW,
            message: {
                content: message,
                intent: Intent.DANGER,
                icon: 'error',
            },
        })
    }
}

const dispatchCreateRunbookApi = (runbook, dispatch, getState) => {
    const payload = {
        runbook: {
            name: runbook.name,
            environments: runbook.environments,
            customer_id: runbook.orgPk,
            stage: runbook.stage,
            parameters: runbook.parameters,
            first_action: getTopAction(getState(), null).uid,
            ci_uid_map: runbook.actions.ciToUid,
            mode: runbook.mode,
            max_depth: runbook.maxDepth,
            templateId: runbook.templateId,
        },
        actions: runbook.actions.uids.map((id) =>
            formatAction(runbook.actions.byId[id])
        ),
    }
    dispatch({
        type: actionTypes.API,
        method: 'post',
        data: payload,
        payload: {
            url: '/api/runbooks/util/runbook/',
            start: () => ({ type: actionTypes.CREATE_RUNBOOK }),
            success: (payload) => {
                dispatch(replace(`/runbooks/${payload.data.id}/`))
                return {
                    type: actionTypes.RESET_CREATE_RUNBOOK,
                    runbookId: null,
                }
            },
            error: (error) => {
                dispatchErrorToast(dispatch, 'Failed to create runbook.')
                return {
                    type: actionTypes.CREATE_RUNBOOK_ERROR,
                    error,
                }
            },
        },
    })
}

export const createRunbook = () => (dispatch, getState) => {
    // we always get the null id as that's the new runbook
    const runbook = getRunbook(getState(), null)
    validateNotifyUserAndDispatchAction(
        runbook,
        dispatch,
        getState,
        dispatchCreateRunbookApi
    )
}

const dispatchUpdateRunbookApi = (runbook, dispatch, getState) => {
    const data = {
        runbook: {
            name: runbook.name,
            environments: runbook.environments,
            stage: runbook.stage,
            parameters: runbook.parameters,
            admins: runbook.admins.map((a) => a.id),
            admin_teams: runbook.adminTeams.map((a) => a.id),
        },
    }

    dispatch({
        type: actionTypes.API,
        method: 'put',
        data,
        payload: {
            url: `/api/runbooks/util/runbook/${runbook.id}/`,
            start: () => ({
                type: actionTypes.UPDATE_RUNBOOK_SERVER,
                payload: data,
            }),
            success: (payload) => {
                if (payload.data.status === 'ok') {
                    return {
                        type: actionTypes.UPDATE_RUNBOOK_SERVER_RECEIVED,
                        runbookId: runbook.id,
                        payload,
                    }
                }
            },
            error: (error) => {
                dispatchErrorToast(
                    dispatch,
                    'Failed to update runbook on server.'
                )
                return {
                    type: actionTypes.UPDATE_RUNBOOK_SERVER_ERROR,
                    runbookId: runbook.id,
                    error,
                }
            },
        },
    })
}

export const asyncUpdateRunbook = (runbookId) => (dispatch, getState) => {
    const runbook = getRunbook(getState(), runbookId)
    validateNotifyUserAndDispatchAction(
        runbook,
        dispatch,
        getState,
        dispatchUpdateRunbookApi
    )
}

const dispatchEditRunbookApi = (runbook, dispatch, getState) => {
    const data = {
        runbook: {
            max_depth: runbook.maxDepth,
            ci_uid_map: runbook.actions.ciToUid,
            first_action: runbook.topAction,
            customer_id: runbook.orgPk,
        },
        actions: runbook.actions.uids.map((id) =>
            formatAction(runbook.actions.byId[id])
        ),
    }
    dispatch({
        type: actionTypes.API,
        method: 'put',
        data,
        payload: {
            url: `/api/runbooks/util/runbook/${runbook.id}/actions/`,
            start: () => ({
                type: actionTypes.EDIT_RUNBOOK_ACTIONS,
                payload: data,
            }),
            success: (payload) => {
                if (payload.data.status === 'ok') {
                    return {
                        type: actionTypes.EDIT_RUNBOOK_ACTIONS_RECEIVED,
                        runbookId: runbook.id,
                        payload,
                    }
                }
            },
            error: (error) => {
                dispatchErrorToast(
                    dispatch,
                    'Failed to edit runbook actions. Runbook is not currently synced.'
                )
                return {
                    type: actionTypes.EDIT_RUNBOOK_ACTIONS_ERROR,
                    runbookId: runbook.id,
                    error,
                }
            },
        },
    })
}

export const asyncEditRunbookActions = (runbookId) => (dispatch, getState) => {
    // remove actions marked for deletion
    dispatch({
        type: actionTypes.DELETE_MARKED_ACTIONS,
        runbookId,
    })
    const runbook = getRunbook(getState(), runbookId)
    validateNotifyUserAndDispatchAction(
        runbook,
        dispatch,
        getState,
        dispatchEditRunbookApi
    )
}

export const asyncUpdateRunbookSession = (
    runbookId,
    actionUids,
    operation,
    hiddenActionUids = []
) => {
    return (dispatch) => {
        const data = {
            operation,
            action_uids: actionUids,
            hidden_action_uids: hiddenActionUids, // NEW: Include hidden actions
        }

        dispatch({
            type: actionTypes.UPDATE_RUNBOOK_SESSION_ACTIONS,
            runbookId,
            payload: data,
        })

        dispatch({
            type: actionTypes.API,
            method: 'put',
            data,
            payload: {
                url: `/api/runbooks/util/runbook/${runbookId}/session/actions/`,
                start: () => ({
                    type: actionTypes.UPDATE_RUNBOOK_SESSION_ACTIONS,
                    payload: data,
                }),
                success: (payload) => {
                    if (payload.data.status === 'ok') {
                        return {
                            type:
                                actionTypes.UPDATE_RUNBOOK_SESSION_ACTIONS_RECEIVED,
                            runbookId,
                            payload,
                        }
                    }
                },
                error: (error) => {
                    dispatchErrorToast(
                        dispatch,
                        'Failed to update runbook session actions. Runbook is not currently synced.'
                    )
                    return {
                        type: actionTypes.UPDATE_RUNBOOK_SESSION_ACTIONS_ERROR,
                        runbookId,
                        error,
                    }
                },
            },
        })
    }
}

export const applyTemplateActions = (runbookId, templateActions) => {
    const {
        byId,
        uids,
        allUids,
        firstAction,
        lastUid,
        maxDepth,
    } = transformTemplateActions(templateActions)

    return {
        type: actionTypes.APPLY_TEMPLATE_ACTIONS,
        id: runbookId,
        byId,
        uids,
        allUids,
        firstAction,
        lastUid,
        maxDepth,
    }
}

export const copyRunbook = (runbookId, newName) => (dispatch) => {
    const data = {
        name: newName,
    }
    dispatch({
        type: actionTypes.TOAST_SHOW,
        message: {
            content:
                'Started copying the runbook... This might take a while, so please stand by.',
            intent: Intent.PRIMARY,
            icon: 'info-sign',
        },
    })
    dispatch({
        type: actionTypes.API,
        method: 'put',
        data,
        payload: {
            url: `/api/runbooks/util/runbook/${runbookId}/copy/`,
            start: () => ({
                type: actionTypes.COPY_RUNBOOK_SERVER,
                payload: data,
            }),
            success: (payload) => {
                if (payload.data.status === 'ok') {
                    dispatch({
                        type: actionTypes.TOAST_SHOW,
                        message: {
                            content: 'Copy was successful.',
                            intent: Intent.SUCCESS,
                            icon: 'info-sign',
                        },
                    })
                    dispatch(getRunbooksFromServer())
                    return {
                        type: actionTypes.COPY_RUNBOOK_SERVER_RECEIVED,
                        runbookId,
                        payload,
                    }
                }
            },
            error: (error) => {
                dispatchErrorToast(
                    dispatch,
                    'We encountered an error during the copy process.'
                )
                return {
                    type: actionTypes.COPY_RUNBOOK_SERVER_ERROR,
                    runbookId,
                    error,
                }
            },
        },
    })
}

export const exportRunbookLegacy = (runbookId, runbookName) => (dispatch) => {
    dispatch({
        type: actionTypes.API,
        method: 'get',
        payload: {
            url: `/api/runbooks/export/${runbookId}/`,
            start: () => ({
                type: actionTypes.EXPORT_RUNBOOK_LEGACY,
            }),
            success: (payload) => {
                let { data } = payload
                const header = 'data:text/csv;charset=utf-8'
                data = `${header},${data}`
                data = encodeURI(data)
                const link = document.createElement('a')
                link.setAttribute('href', data)
                link.setAttribute(
                    'download',
                    `export_runbook_${runbookId}_${runbookName}.csv`
                )
                link.click()
                return {
                    type: actionTypes.TOAST_SHOW,
                    message: {
                        content: `Successfully exported ${runbookName}`,
                        intent: 'success',
                    },
                }
            },
            error: (error) =>
                createErrorToast(`Cannot export runbook ${runbookName}`),
        },
    })
}

export const exportRunbook = (runbookId, runbookName) => (dispatch) => {
    dispatch({
        type: actionTypes.API,
        method: 'get',
        payload: {
            url: `/api/runbooks/util/runbook/${runbookId}/`,
            start: () => ({ type: actionTypes.GET_RUNBOOK, runbookId }),
            success: (payload) => {
                dispatch({
                    type: actionTypes.GET_RUNBOOK_RECEIVED,
                    runbookId,
                    data: payload.data,
                })
                dispatch({
                    type: actionTypes.EXPORT_RUNBOOK,
                    runbookId,
                })
                return {
                    type: actionTypes.TOAST_SHOW,
                    message: {
                        content: `Successfully exported ${runbookName}`,
                        intent: 'success',
                    },
                }
            },
            error: () =>
                createErrorToast(`Cannot export runbook ${runbookName}`),
        },
    })
}

export const exportListOfRunbooks = () => (dispatch) => {
    dispatch({
        type: actionTypes.API,
        method: 'get',
        payload: {
            url: `${urls.RUNBOOKS_URL}?per_page=1000`,
            start: () => ({
                type: actionTypes.TOAST_SHOW,
                message: {
                    content: `The export has started, it will create a download file shortly.`,
                    intent: Intent.PRIMARY,
                },
            }),
            success: (payload) => {
                dispatch({
                    type: actionTypes.EXPORT_LIST_OF_RUNBOOKS_SUCCESS,
                    payload: payload,
                })
                return {
                    type: actionTypes.TOAST_SHOW,
                    message: {
                        content: `Successfully exported list of runbooks`,
                        intent: 'success',
                    },
                }
            },
            error: () => ({
                type: actionTypes.TOAST_SHOW,
                message: {
                    content: `Cannot export runbook list of runbooks`,
                    intent: Intent.DANGER,
                },
            }),
        },
    })
}

// Helper function to fetch logs
const fetchLogs = async (url, logType) => {
    try {
        const response = await axios.get(url, {
            headers: { ...axios.defaults.headers },
        })
        const logsRaw = response.data.results

        switch (logType) {
            case LOGS.COMMENT:
                return logsRaw.map((log) => ({
                    createdBy: log.created_by,
                    message: log.message,
                    logType: log.log_type,
                    id: log.id,
                    createdAt: moment(log.date_created).format(
                        TIMESTAMP_FORMAT
                    ),
                    uid: log.action_uid,
                }))
            case LOGS.DECISIONS:
                return logsRaw.map((log) => ({
                    id: log.id,
                    decision: log.decision,
                    createdAt: moment(log.logged).format(TIMESTAMP_FORMAT),
                    owner: log.owner,
                    reasoning: log.reasoning,
                    sessionId: log.session,
                    actionUid: log.action_uid,
                }))
            case LOGS.ISSUE:
                return logsRaw.map((log) => ({
                    id: log.id,
                    due: moment(log.due).toISOString(),
                    description: log.description,
                    solution: log.solution,
                    createdAt: moment(log.registered).format(TIMESTAMP_FORMAT),
                    owner: log.owner,
                    priority: log.priority,
                    severity: log.severity,
                    status: log.status,
                    actionUid: log.action_uid,
                }))
            case LOGS.ACTIVITIES:
                return logsRaw.map((log) => ({
                    id: log.id,
                    source: log.source,
                    date: moment(log.date).toISOString(),
                    description: log.description,
                    owner: log.owner,
                    company: log.company,
                    priority: log.priority,
                    due: moment(log.due).toISOString(),
                    comments: log.comments,
                    closed: log.closed,
                    status: log.status,
                    actionUid: log.action_uid,
                    sessionId: log.session,
                    createdAt: moment().format(TIMESTAMP_FORMAT),
                }))
            default:
                console.warn('Unknown log type:', logType)
                return null
        }
    } catch (error) {
        console.error('Error fetching logs:', error)
        throw error
    }
}
/**
 * Processes logs by attaching the corresponding action object to each log entry.
 * @param {Object} state - The Redux state object.
 * @param {Object} runbook - The runbook object containing runbook details.
 * @param {Object} logsByType - An object mapping log types to arrays of log entries.
 * @returns {Object} An object mapping log types to arrays of logs with attached action objects.
 */
const processLogsWithActions = (state, runbook, logsByType) => {
    const mapLogsWithAction = (logs, logKey) => {
        return logs.map((log) => ({
            ...log,
            action: getAction(state, runbook.id, log[logKey]),
        }))
    }

    const logTypes = {
        [LOGS.COMMENT]: { key: 'uid' },
        [LOGS.DECISIONS]: { key: 'actionUid' },
        [LOGS.ISSUE]: { key: 'actionUid' },
        [LOGS.ACTIVITIES]: { key: 'actionUid' },
    }

    const processed = {}

    // Only process the log types present in logsByType
    Object.keys(logsByType).forEach((logType) => {
        const logs = logsByType[logType]
        const key = logTypes[logType]?.key
        processed[logType] = key ? mapLogsWithAction(logs, key) : logs
    })

    return processed
}

// Function to export logs
export const exportLogs = (runbook, selectedWindow) => async (
    dispatch,
    getState
) => {
    const logUrls = {
        [LOGS.COMMENT]: `/api/runbooks/${runbook.activeSession}/action-logs/?log_type=1&page=1&per_page=${runbook.totalElements}`,
        [LOGS.DECISIONS]: `/api/runbooks/${runbook.activeSession}/decision-logs?page=1&per_page=${runbook.totalElements}`,
        [LOGS.ISSUE]: `/api/runbooks/${runbook.activeSession}/issue-logs?page=1&per_page=${runbook.totalElements}`,
        [LOGS.ACTIVITIES]: `/api/runbooks/${runbook.activeSession}/activities-logs?page=1&per_page=${runbook.totalElements}`,
    }

    const { activeRunbook } = getState().ui.runbook
    const state = getState()

    try {
        const fetchedLogs = await fetchLogs(
            logUrls[selectedWindow],
            selectedWindow
        )

        const processedLogs = processLogsWithActions(state, runbook, {
            [selectedWindow]: fetchedLogs,
        })

        const logActionMap = {
            [LOGS.COMMENT]: actionTypes.EXPORT_COMMENT_LOGS,
            [LOGS.DECISIONS]: actionTypes.EXPORT_DECISION_LOGS,
            [LOGS.ISSUE]: actionTypes.EXPORT_ISSUE_LOGS,
            [LOGS.ACTIVITIES]: actionTypes.EXPORT_ACTIVITIES_LOGS,
        }

        dispatch({
            type: logActionMap[selectedWindow],
            data: processedLogs[selectedWindow],
            runbookId: activeRunbook,
        })

        dispatch({
            type: actionTypes.TOAST_SHOW,
            message: {
                content: 'Successfully exported runbook logs',
                intent: Intent.SUCCESS,
            },
        })
    } catch (error) {
        console.error(error)
        dispatch({
            type: actionTypes.TOAST_SHOW,
            message: {
                content: 'Cannot export runbook logs',
                intent: Intent.DANGER,
            },
        })
    }
}
const fetchRunbookActionList = async (dispatch, runbookId) => {
    await dispatch({
        type: actionTypes.API,
        method: 'get',
        payload: {
            url: `/api/runbooks/util/runbook/${runbookId}/`,
            start: () => ({ type: actionTypes.GET_RUNBOOK, runbookId }),
            success: (payload) => {
                dispatch({
                    type: actionTypes.GET_RUNBOOK_RECEIVED,
                    runbookId,
                    data: payload.data,
                })
            },
            error: (error) => {
                console.error('Failed to fetch runbook data', error)
                throw error
            },
        },
    })
}

const fetchAllLogs = async (runbook, perPage) => {
    const logFetchers = {
        'Comment log': () =>
            fetchLogs(
                `/api/runbooks/${runbook.activeSession}/action-logs/?log_type=1&page=1&per_page=${perPage}`,
                LOGS.COMMENT
            ),
        'Issue log': () =>
            fetchLogs(
                `/api/runbooks/${runbook.activeSession}/issue-logs?page=1&per_page=${perPage}`,
                LOGS.ISSUE
            ),
        'Decisions log': () =>
            fetchLogs(
                `/api/runbooks/${runbook.activeSession}/decision-logs?page=1&per_page=${perPage}`,
                LOGS.DECISIONS
            ),
        'Activities log': () =>
            fetchLogs(
                `/api/runbooks/${runbook.activeSession}/activities-logs?page=1&per_page=${perPage}`,
                LOGS.ACTIVITIES
            ),
    }

    const logResults = await Promise.all(
        Object.entries(logFetchers).map(([name, fetcher]) => fetcher())
    )

    return Object.keys(logFetchers).map((name, idx) => ({
        name,
        data: logResults[idx],
    }))
}

export const exportAllRunbook = (runbook) => async (dispatch, getState) => {
    try {
        const runbookId = runbook.id
        const runbookName = runbook.name

        await fetchRunbookActionList(dispatch, runbookId)

        const state = getState()
        const runbookData = state.runbooks.byId[runbookId]

        // Format Action List
        const actionList = transformRunbookData(runbookData)
        const datasets = [{ name: 'Action list', data: actionList }]

        // Fetch all logs
        const logs = await fetchAllLogs(runbook, 1000)

        // Convert fetched logs array → logType map for reuse
        const logsByType = {}
        for (const { name, data } of logs) {
            if (name.includes('Comment')) logsByType[LOGS.COMMENT] = data
            if (name.includes('Issue')) logsByType[LOGS.ISSUE] = data
            if (name.includes('Decisions')) logsByType[LOGS.DECISIONS] = data
            if (name.includes('Activities')) logsByType[LOGS.ACTIVITIES] = data
        }

        // Process logs (add actions)
        const processedLogs = processLogsWithActions(state, runbook, logsByType)

        // Transform logs for Excel
        const transformedCommentLogData = transformCommentLogData(runbookData, {
            runbookId: runbookId,
            data: processedLogs[LOGS.COMMENT] || [],
        })
        const transformedIssueLogData = transformIssueLogData(runbookData, {
            runbookId: runbookId,
            data: processedLogs[LOGS.ISSUE] || [],
        })
        const transformedDecisionsLogData = transformDecisionsLogData(
            runbookData,
            { runbookId: runbookId, data: processedLogs[LOGS.DECISIONS] || [] }
        )
        const transformedActivitiesLogData = transformActivitiesLogData(
            runbookData,
            { runbookId: runbookId, data: processedLogs[LOGS.ACTIVITIES] || [] }
        )

        // Append to Excel datasets
        datasets.push(
            { name: 'Comment log', data: transformedCommentLogData },
            { name: 'Issue log', data: transformedIssueLogData },
            { name: 'Decisions log', data: transformedDecisionsLogData },
            { name: 'Activities log', data: transformedActivitiesLogData }
        )

        const fileName = `runbook_all_data_${runbookId}_${runbookName}.xlsx`
        // Generate Excel file
        generateExcelFile(datasets, fileName)

        dispatch({
            type: actionTypes.TOAST_SHOW,
            message: {
                content: 'Export completed successfully!',
                intent: Intent.SUCCESS,
            },
        })
    } catch (error) {
        console.error('Export all failed', error)
        dispatch({
            type: actionTypes.TOAST_SHOW,
            message: {
                content: 'Export failed',
                intent: Intent.DANGER,
            },
        })
    }
}

export const getRunbookFromServer = (runbookId, callback, parameters) => (
    dispatch
) => {
    dispatch({
        type: actionTypes.API,
        method: 'get',
        payload: {
            url: `/api/runbooks/util/runbook/${runbookId}/`,
            start: () => ({ type: actionTypes.GET_RUNBOOK, runbookId }),
            success: (payload) => {
                const response = {
                    type: actionTypes.GET_RUNBOOK_RECEIVED,
                    runbookId,
                    data: payload.data,
                }
                dispatch({
                    type: actionTypes.SET_ACTIVE_RUNBOOK,
                    runbookId: parseInt(runbookId),
                })

                if (callback) {
                    dispatch(response)
                    dispatch(updateRunbook(runbookId, { parameters }))
                    return callback
                } else {
                    return response
                }
            },
            error: (error) => {
                const message =
                    error.response?.status === 404
                        ? 'The requested runbook does not exist.'
                        : 'The current runbook is invalid. Cannot load it.'
                dispatchErrorToast(dispatch, message)
                return {
                    type: actionTypes.GET_RUNBOOK_ERROR,
                    runbookId,
                    error,
                }
            },
        },
    })
}

export const deleteRunbookFromServer = (dispatch, runbookId) => {
    return {
        type: actionTypes.API,
        method: 'delete',
        payload: {
            url: `/api/runbooks/util/runbook/${runbookId}/`,
            start: () => ({ type: actionTypes.DELETE_RUNBOOK }),
            success: (payload) => ({
                type: actionTypes.DELETE_RUNBOOK_RECEIVED,
                runbookId,
                data: payload.data,
            }),
            error: (error) => {
                dispatchErrorToast(dispatch, 'Failed to delete runbook.')
                return {
                    type: actionTypes.DELETE_RUNBOOK_ERROR,
                    runbookId,
                    error,
                }
            },
        },
    }
}

export const getCustomerSolutions = (runbookId) => (dispatch, getState) => {
    const runbook = getRunbook(getState(), runbookId)
    const customerId = runbook.orgPk || null
    // const customerId = 656
    dispatch({
        type: actionTypes.API,
        method: 'get',
        payload: {
            url: `${urls.SOLUTIONS_URL}${customerId}/`,
            start: () => ({ type: actionTypes.GET_CUSTOMER_SOLUTIONS_RUNBOOK }),
            success: (payload) => ({
                type: actionTypes.GET_CUSTOMER_SOLUTIONS_RUNBOOK_SUCCESS,
                payload,
                customerId,
                runbookId,
                runbook,
            }),
            error: (error) => {
                dispatchErrorToast(
                    dispatch,
                    'Failed to get customer solutions for runbook.'
                )
                return {
                    type: actionTypes.GET_CUSTOMER_SOLUTIONS_RUNBOOK_ERROR,
                    error,
                    runbookId,
                }
            },
        },
    })
}

export const getRunbooksFromServer = ({
    page = 1,
    per_page = 10,
    sort = '',
} = {}) => (dispatch, getState) => {
    const state = getState()
    const { runbooks } = state
    let query = ''
    if (!allAccess(state)) {
        query =
            '?' +
            runbooks.access
                .filter((t) => t.selected)
                .map((t) => `access=${t.value}`)
                .join('&')
    }
    const envQuery = runbooks.stage
        .filter((t) => t.selected && t.value != null)
        .map((t) => `env=${t.value}`)
        .join('&')
    if (envQuery) {
        query += `${query ? '&' : '?'}${envQuery}`
    }
    if (!query) {
        query = '?'
    }
    if (runbooks.customerFilter.length) {
        query +=
            '&' +
            runbooks.customerFilter.map((c) => `customer_id=${c.id}`).join('&')
    }
    if (sort) {
        query += `&sort=${encodeURIComponent(sort)},-last_updated`
    }
    query += `${
        query && query !== '?' ? '&' : ''
    }page=${page}&per_page=${per_page}`
    return dispatch({
        type: actionTypes.API,
        method: 'get',
        payload: {
            url: `${urls.RUNBOOKS_URL}${query}`,
            start: () => ({ type: actionTypes.GET_RUNBOOKS }),
            success: (payload) => ({
                type: actionTypes.GET_RUNBOOKS_SUCCESS,
                payload,
            }),
            error: (error) => {
                console.error(error)
                dispatchErrorToast(
                    dispatch,
                    'Failed to get runbooks from server.'
                )
                return {
                    type: actionTypes.GET_RUNBOOKS_ERROR,
                    error,
                }
            },
        },
        debounce: {
            key: 'getRunbooks',
            wait: 500,
        },
    })
}

export const getRunbookTemplatesFromServer = ({
    page = 1,
    per_page = 10,
    sort = '',
    search = '',
    template_type = '',
    organization = '',
    organization_scope = '',
    runbook_category = '',
    device_type = '',
} = {}) => (dispatch) => {
    const query = buildQueryString({
        page,
        per_page,
        sort,
        search,
        template_type,
        organization,
        organization_scope,
        runbook_category,
        device_type,
    })
    const requestKey = JSON.stringify({
        page,
        sort,
        search,
        template_type,
        organization,
        organization_scope,
        runbook_category,
        device_type,
    })

    return dispatch({
        type: actionTypes.API,
        method: 'get',
        payload: {
            url: `/api/runbooks/templates/${query}`,
            start: () => ({
                type: actionTypes.GET_RUNBOOK_TEMPLATES,
                page,
                sort,
                requestKey,
            }),
            success: (payload) => ({
                type: actionTypes.GET_RUNBOOK_TEMPLATES_SUCCESS,
                payload,
                sort,
                requestKey,
            }),
            error: (error) => {
                console.error(error)
                dispatchErrorToast(dispatch, 'Failed to get runbook templates.')
                return {
                    type: actionTypes.GET_RUNBOOK_TEMPLATES_ERROR,
                    error,
                }
            },
        },
    })
}

export const getRunbookTemplateFromServer = (templateId) => (dispatch) => {
    return dispatch({
        type: actionTypes.API,
        method: 'get',
        payload: {
            url: `/api/runbooks/templates/${templateId}/`,
            start: () => ({
                type: actionTypes.GET_RUNBOOK_TEMPLATE,
                templateId,
            }),
            success: (payload) => ({
                type: actionTypes.GET_RUNBOOK_TEMPLATE_SUCCESS,
                templateId,
                payload,
            }),
            error: (error) => {
                console.error(error)
                dispatchErrorToast(
                    dispatch,
                    'Failed to load runbook template details.'
                )
                return {
                    type: actionTypes.GET_RUNBOOK_TEMPLATE_ERROR,
                    templateId,
                    error,
                }
            },
        },
    })
}

export const getRunbookTemplateActionsFromServer = ({
    templateId,
    page = 1,
    per_page = 1000,
} = {}) => (dispatch) => {
    const query = buildQueryString({ page, per_page })

    return dispatch({
        type: actionTypes.API,
        method: 'get',
        payload: {
            url: `/api/runbooks/templates/${templateId}/actions/${query}`,
            start: () => ({
                type: actionTypes.GET_RUNBOOK_TEMPLATE_ACTIONS,
                templateId,
            }),
            success: (payload) => ({
                type: actionTypes.GET_RUNBOOK_TEMPLATE_ACTIONS_SUCCESS,
                templateId,
                payload,
            }),
            error: (error) => {
                console.error(error)
                dispatchErrorToast(
                    dispatch,
                    'Failed to load runbook template actions.'
                )
                return {
                    type: actionTypes.GET_RUNBOOK_TEMPLATE_ACTIONS_ERROR,
                    templateId,
                    error,
                }
            },
        },
    })
}

export const saveRunbookTemplateActions = ({
    templateId,
    actions = [],
    page = 1,
    per_page = 1000,
    shouldRefetch = true,
} = {}) => async (dispatch) => {
    dispatch({
        type: actionTypes.SAVE_RUNBOOK_TEMPLATE_ACTIONS,
        templateId,
    })

    try {
        await Promise.all(
            actions.map((item) =>
                axios.patch(
                    `/api/runbooks/templates/${templateId}/actions/${item.id}/`,
                    {
                        parent: item.parent,
                        sequence_id: item.sequence_id,
                        weight: item.sequence_id,
                    },
                    {
                        headers: { ...axios.defaults.headers },
                    }
                )
            )
        )

        dispatch({
            type: actionTypes.SAVE_RUNBOOK_TEMPLATE_ACTIONS_SUCCESS,
            templateId,
        })

        if (shouldRefetch) {
            dispatch(
                getRunbookTemplateActionsFromServer({
                    templateId,
                    page,
                    per_page,
                })
            )
        }

        dispatch({
            type: actionTypes.TOAST_SHOW,
            message: {
                content: 'Template actions saved successfully.',
                intent: Intent.SUCCESS,
            },
        })
    } catch (error) {
        dispatch({
            type: actionTypes.SAVE_RUNBOOK_TEMPLATE_ACTIONS_ERROR,
            templateId,
            error,
        })
        dispatchErrorToast(dispatch, 'Failed to save template actions.')
        throw error
    }
}

export const createTemplateActionOnServer = (
    templateId,
    { actions = [] } = {}
) => async (dispatch) => {
    const normalizedActions = (actions || [])
        .map((item) => ({
            name: item?.name,
            parent: item?.parent ?? null,
            sequence_id: item?.sequence_id,
            weight: item?.weight ?? item?.sequence_id,
        }))
        .filter(
            (item) =>
                String(item.name || '').trim() &&
                Number.isFinite(Number(item.sequence_id))
        )

    if (!normalizedActions.length) {
        return []
    }

    try {
        const payload = await axios.post(
            `/api/runbooks/templates/${templateId}/actions/bulk-create/`,
            {
                actions: normalizedActions,
            },
            {
                headers: { ...axios.defaults.headers },
            }
        )

        dispatch({
            type: actionTypes.TOAST_SHOW,
            message: {
                content: 'Template actions saved successfully.',
                intent: Intent.SUCCESS,
            },
        })

        return payload.data
    } catch (error) {
        dispatchErrorToast(dispatch, 'Failed to add template action.')
        throw error
    }
}

export const deleteRunbookTemplateFromServer = (templateId) => (dispatch) => {
    return dispatch({
        type: actionTypes.API,
        method: 'delete',
        payload: {
            url: `/api/runbooks/templates/${templateId}/`,
            start: () => ({ type: actionTypes.DELETE_RUNBOOK_TEMPLATE }),
            success: (payload) => ({
                type: actionTypes.DELETE_RUNBOOK_TEMPLATE_RECEIVED,
                templateId,
                payload,
            }),
            error: (error) => {
                dispatchErrorToast(
                    dispatch,
                    'Failed to delete runbook template.'
                )
                return {
                    type: actionTypes.DELETE_RUNBOOK_TEMPLATE_ERROR,
                    templateId,
                    error,
                }
            },
        },
    })
}
export const deleteTemplateActionFromServer = ({
    templateId,
    actionIds = [],
    page = 1,
    per_page = 1000,
    shouldRefetch = true,
} = {}) => async (dispatch) => {
    await Promise.all(
        actionIds.map((actionId) =>
            axios.delete(
                `/api/runbooks/templates/${templateId}/actions/${actionId}/`,
                {
                    headers: { ...axios.defaults.headers },
                }
            )
        )
    )

    if (shouldRefetch) {
        dispatch(
            getRunbookTemplateActionsFromServer({
                templateId,
                page,
                per_page,
            })
        )
    }

    dispatch({
        type: actionTypes.TOAST_SHOW,
        message: {
            content: 'Template actions saved successfully.',
            intent: Intent.SUCCESS,
        },
    })
}
export const renameRunbookTemplateActions = ({
    templateId,
    actions = [],
    page = 1,
    per_page = 1000,
    shouldRefetch = true,
} = {}) => async (dispatch) => {
    await Promise.all(
        actions.map((item) =>
            axios.put(
                `/api/runbooks/templates/${templateId}/actions/${item.id}/`,
                {
                    name: item.name,
                },
                {
                    headers: { ...axios.defaults.headers },
                }
            )
        )
    )

    if (shouldRefetch) {
        dispatch(
            getRunbookTemplateActionsFromServer({
                templateId,
                page,
                per_page,
            })
        )
    }

    dispatch({
        type: actionTypes.TOAST_SHOW,
        message: {
            content: 'Template actions saved successfully.',
            intent: Intent.SUCCESS,
        },
    })
}

export const createRunbookTemplate = (data) => async (dispatch) => {
    dispatch({ type: actionTypes.CREATE_RUNBOOK_TEMPLATE })

    try {
        const payload = await axios.post(
            '/api/runbooks/templates/create/',
            data,
            {
                headers: { ...axios.defaults.headers },
            }
        )

        dispatch({
            type: actionTypes.CREATE_RUNBOOK_TEMPLATE_SUCCESS,
            payload,
        })

        // Refresh templates from server after a successful create.
        dispatch(getRunbookTemplatesFromServer())

        dispatch({
            type: actionTypes.TOAST_SHOW,
            message: {
                content: 'Runbook template created successfully.',
                intent: Intent.SUCCESS,
            },
        })

        return payload.data
    } catch (error) {
        dispatchErrorToast(dispatch, 'Failed to create runbook template.')
        dispatch({
            type: actionTypes.CREATE_RUNBOOK_TEMPLATE_ERROR,
            error,
        })
        throw error
    }
}

export const updateRunbookTemplate = (templateId, data) => async (dispatch) => {
    dispatch({ type: actionTypes.UPDATE_RUNBOOK_TEMPLATE, templateId })

    try {
        const payload = await axios.patch(
            `/api/runbooks/templates/${templateId}/`,
            data,
            {
                headers: { ...axios.defaults.headers },
            }
        )

        dispatch({
            type: actionTypes.UPDATE_RUNBOOK_TEMPLATE_SUCCESS,
            templateId,
            payload,
        })

        dispatch(getRunbookTemplatesFromServer())

        dispatch({
            type: actionTypes.TOAST_SHOW,
            message: {
                content: 'Runbook template updated successfully.',
                intent: Intent.SUCCESS,
            },
        })

        return payload.data
    } catch (error) {
        dispatch({
            type: actionTypes.UPDATE_RUNBOOK_TEMPLATE_ERROR,
            templateId,
            error,
        })
        throw error
    }
}

export const swapRunbookTemplatePriority = (id1, id2) => async () => {
    const payload = await axios.post('/api/runbooks/templates/swap-priority/', {
        id1,
        id2,
    })

    return payload.data
}

export const getAllHistoryLogsFromServer = (
    runbookSessionId,
    logType,
    page = 1,
    per_page = 10
) => (dispatch, getState) => {
    const { activeRunbook } = getState().ui.runbook
    const query = `&page=${page}&per_page=${per_page}`
    return dispatch({
        type: actionTypes.API,
        method: 'get',
        payload: {
            url: `/api/runbooks/${runbookSessionId}/action-logs/?log_type=${logType}${query}`,
            start: () => ({ type: actionTypes.GET_ALL_ACTION_LOGS }),
            success: (payload) => ({
                type: actionTypes.GET_ALL_ACTION_LOGS_SUCCESS,
                payload,
                runbookId: activeRunbook,
            }),
            error: (error) => {
                dispatchErrorToast(dispatch, 'Failed to get all action logs.')
                return {
                    type: actionTypes.GET_ALL_ACTION_LOGS_ERROR,
                    error,
                    runbookId: activeRunbook,
                }
            },
        },
    })
}

export const getRunbooksDecorator = (wrapped) => (...args) => (
    dispatch,
    getState
) => {
    const result = wrapped(...args)
    dispatch(result)
    dispatch(getRunbooksFromServer())
}

export const toggleAccessType = (access) => ({
    type: actionTypes.TOGGLE_RUNBOOKS_ACCESS_TYPE,
    payload: { access },
})

export const toggleEnvironmentFilter = (env) => ({
    type: actionTypes.TOGGLE_RUNBOOKS_ENVIRONMENT,
    payload: { env },
})

export const setCustomerFilter = (customers) => ({
    type: actionTypes.SET_CUSTOMER_FILTER,
    payload: { customers },
})

export const setDepthLevel = (runbookId, selectedDepth) => ({
    type: actionTypes.SET_DEPTH_LEVEL,
    payload: { selectedDepth },
    runbookId,
})

export const getHistoryLogs = (state, page = 1, per_page = 10) =>
    state.runbooks.byId[state.ui.runbook.activeRunbook].actionLogs.map(
        (log) => ({
            ...log,
            action: getAction(state, state.ui.runbook.activeRunbook, log.uid),
        })
    )

export const getAllDecisionLogsFromServer = (
    runbookSessionId,
    page = 1,
    per_page = 10
) => (dispatch, getState) => {
    const { activeRunbook } = getState().ui.runbook
    const query = `?page=${page}&per_page=${per_page}`

    return dispatch({
        type: actionTypes.API,
        method: 'get',
        payload: {
            url: `/api/runbooks/${runbookSessionId}/decision-logs/${query}`,
            start: () => ({ type: actionTypes.GET_ALL_DECISION_LOGS }),
            success: (payload) => {
                return {
                    type: actionTypes.GET_ALL_DECISION_LOGS_SUCCESS,
                    payload,
                    runbookId: activeRunbook,
                }
            },
            error: (error) => {
                dispatchErrorToast(dispatch, 'Failed to get all decision logs.')
                return {
                    type: actionTypes.GET_ALL_DECISION_LOGS_ERROR,
                    error,
                    runbookId: activeRunbook,
                }
            },
        },
    })
}

export const getDecisionLogs = (state) => {
    let decisionLogs =
        state.runbooks.byId[state.ui.runbook.activeRunbook].decisionLogs
    if (decisionLogs && decisionLogs.length) {
        return decisionLogs.map((log) => {
            return {
                ...log,
                // TODO: refactor this once actions are asynchronously fetched.
                action: getAction(
                    state,
                    state.ui.runbook.activeRunbook,
                    log.actionUid
                ),
            }
        })
    }
}

export const createDecisionLog = (sessionId, data) => (dispatch, getState) => {
    data.session = sessionId

    return dispatch({
        type: actionTypes.API,
        method: 'post',
        data: {
            owner: data.owner,
            decision: data.decision,
            reasoning: data.reasoning,
            action_uid: data.actionUid,
            session: data.session,
        },
        payload: {
            url: `/api/runbooks/${data.session}/decision-logs/`,
            success: (payload) => {
                dispatch(getAllDecisionLogsFromServer(data.session))
                return {
                    type: actionTypes.TOAST_SHOW,
                    message: {
                        content: 'Successfully logged decision',
                        intent: 'success',
                        icon: 'tick',
                    },
                }
            },
            error: (error) =>
                createErrorToast(
                    'Something went wrong. Your decision was not logged.'
                ),
        },
    })
}

export const updateDecisionLog = (sessionId, data) => (dispatch, getState) => {
    data.session = sessionId

    if (!data.id) {
        return console.error(
            'DecisionLog not updated. Please pass an ID.',
            data
        )
    }
    return dispatch({
        type: actionTypes.API,
        method: 'put',
        data: data,
        payload: {
            url: `/api/runbooks/${data.session}/decision-logs/${data.id}/`,
            success: (payload) => {
                dispatch(getAllDecisionLogsFromServer(data.session))
                return {
                    type: actionTypes.TOAST_SHOW,
                    message: {
                        content: 'Successfully updated decision.',
                        intent: 'success',
                        icon: 'tick',
                    },
                }
            },
            error: (error) =>
                createErrorToast(
                    'Something went wrong. Your decision was not updated.'
                ),
        },
    })
}

export const getAllIssueLogsFromServer = (
    runbookSessionId,
    page = 1,
    per_page = 10
) => (dispatch, getState) => {
    const { activeRunbook } = getState().ui.runbook
    const query = `?page=${page}&per_page=${per_page}`
    return dispatch({
        type: actionTypes.API,
        method: 'get',
        payload: {
            url: `/api/runbooks/${runbookSessionId}/issue-logs/${query}`,
            start: () => ({ type: actionTypes.GET_ALL_ISSUE_LOGS }),
            success: (payload) => {
                return {
                    type: actionTypes.GET_ALL_ISSUE_LOGS_SUCCESS,
                    payload,
                    runbookId: activeRunbook,
                }
            },
            error: (error) => {
                dispatchErrorToast(dispatch, 'Failed to get all issue logs.')
                return {
                    type: actionTypes.GET_ALL_ISSUE_LOGS_ERROR,
                    error,
                    runbookId: activeRunbook,
                }
            },
        },
    })
}

export const getIssueLogs = (state) => {
    let issueLogs =
        state.runbooks.byId[state.ui.runbook.activeRunbook].issueLogs
    if (issueLogs && issueLogs.length) {
        return issueLogs.map((log) => {
            return {
                ...log,
                // TODO: refactor this once actions are asynchronously fetched.
                action: getAction(
                    state,
                    state.ui.runbook.activeRunbook,
                    log.actionUid
                ),
            }
        })
    }
}

export const createIssueLog = (sessionId, data) => (dispatch, getState) => {
    data.session = sessionId

    return dispatch({
        type: actionTypes.API,
        method: 'post',
        data: {
            description: data.description,
            due: moment(data.due).format(TIMESTAMP_FORMAT),
            owner: data.owner,
            priority: data.priority,
            severity: data.severity,
            status: data.status,
            session: data.session,
            solution: data.solution,
            action_uid: data.actionUid,
        },
        payload: {
            url: `/api/runbooks/${data.session}/issue-logs/`,
            success: (payload) => {
                dispatch(getAllIssueLogsFromServer(data.session))
                return {
                    type: actionTypes.TOAST_SHOW,
                    message: {
                        content: 'Successfully logged issue',
                        intent: 'success',
                        icon: 'tick',
                    },
                }
            },
            error: (error) => {
                createErrorToast(
                    'Something went wrong. Your issue was not logged.'
                )
            },
        },
    })
}

export const updateIssueLog = (sessionId, data) => (dispatch, getState) => {
    data.session = sessionId

    if (!data.id) {
        return console.error('IssueLog not updated. Please pass an ID.', data)
    }
    return dispatch({
        type: actionTypes.API,
        method: 'put',
        data: data,
        payload: {
            url: `/api/runbooks/${data.session}/issue-logs/${data.id}/`,
            success: (payload) => {
                dispatch(getAllIssueLogsFromServer(data.session))
                return {
                    type: actionTypes.TOAST_SHOW,
                    message: {
                        content: 'Successfully updated issue.',
                        intent: 'success',
                        icon: 'tick',
                    },
                }
            },
            error: (error) =>
                createErrorToast(
                    'Something went wrong. Your issue was not updated.'
                ),
        },
    })
}

export const getAllActivityLogsFromServer = (
    runbookSessionId,
    page = 1,
    per_page = 10
) => (dispatch, getState) => {
    const { activeRunbook } = getState().ui.runbook
    const query = `?page=${page}&per_page=${per_page}`
    return dispatch({
        type: actionTypes.API,
        method: 'get',
        payload: {
            url: `/api/runbooks/${runbookSessionId}/activities-logs/${query}`,
            start: () => ({ type: actionTypes.GET_ALL_ACTIVITY_LOGS }),
            success: (payload) => {
                return {
                    type: actionTypes.GET_ALL_ACTIVITY_LOGS_SUCCESS,
                    payload,
                    runbookId: activeRunbook,
                }
            },
            error: (error) => {
                dispatchErrorToast(dispatch, 'Failed to get all activity logs.')
                return {
                    type: actionTypes.GET_ALL_ACTIVITY_LOGS_ERROR,
                    error,
                    runbookId: activeRunbook,
                }
            },
        },
    })
}

export const getActivityLogs = (state) => {
    let activityLogs =
        state.runbooks.byId[state.ui.runbook.activeRunbook].activityLogs
    if (activityLogs && activityLogs.length) {
        return activityLogs.map((log) => {
            return {
                ...log,
                // TODO: refactor this once actions are asynchronously fetched.
                action: getAction(
                    state,
                    state.ui.runbook.activeRunbook,
                    log.actionUid
                ),
            }
        })
    }
}

export const createActivityLog = (sessionId, data) => (dispatch, getState) => {
    data.session = sessionId

    return dispatch({
        type: actionTypes.API,
        method: 'post',
        data: {
            source: data.source,
            date: moment(data.date).format(TIMESTAMP_FORMAT),
            description: data.description,
            owner: data.owner,
            company: data.company,
            priority: data.priority,
            due: moment(data.due).format(TIMESTAMP_FORMAT),
            comments: data.comments,
            closed: data.closed,
            status: data.status,
            session: data.session,
            action_uid: data.actionUid,
        },
        payload: {
            url: `/api/runbooks/${data.session}/activities-logs/`,
            success: (payload) => {
                dispatch(getAllActivityLogsFromServer(data.session))
                return {
                    type: actionTypes.TOAST_SHOW,
                    message: {
                        content: 'Successfully logged activity',
                        intent: 'success',
                        icon: 'tick',
                    },
                }
            },
            error: (error) =>
                createErrorToast(
                    'Something went wrong. Your activity was not logged.'
                ),
        },
    })
}

export const updateActivityLog = (sessionId, data) => (dispatch) => {
    data.session = sessionId

    if (!data.id) {
        return console.error(
            'ActivityLog not updated. Please pass an ID.',
            data
        )
    }
    return dispatch({
        type: actionTypes.API,
        method: 'put',
        data: data,
        payload: {
            url: `/api/runbooks/${data.session}/activities-logs/${data.id}/`,
            success: (payload) => {
                dispatch(getAllActivityLogsFromServer(data.session))
                return {
                    type: actionTypes.TOAST_SHOW,
                    message: {
                        content: 'Successfully updated activity.',
                        intent: 'success',
                        icon: 'tick',
                    },
                }
            },
            error: (error) => {
                dispatchErrorToast(
                    dispatch,
                    'Something went wrong. Your activity was not updated.'
                )
            },
        },
    })
}

export const generateRunbookFromCsv = (customerId, fileName, rows) => (
    dispatch
) => {
    return dispatch({
        type: actionTypes.API,
        method: 'post',
        data: {
            customerId,
            fileName,
            csv: rows,
        },
        payload: {
            url: `/api/runbooks/import/generate/`,
            start: () => ({
                type: actionTypes.GET_CUSTOMER_SOLUTIONS_RUNBOOK,
            }),
            success: (payload) => {
                return {
                    type: actionTypes.GENERATE_RUNBOOK_CSV_SUCCESS,
                    payload,
                    runbookId: null,
                }
            },
            error: (error) => {
                const errors = error.response?.data?.errors
                if (errors && Array.isArray(errors) && errors.length > 0) {
                    return createErrorToast(
                        errors
                            .map((e) => `Row ${e.row}: ${e.message}`)
                            .join('\n'),
                        { timeout: 0, errors }
                    )
                }
                let message = error.response?.data?.message
                    ? error.response?.data?.message
                    : 'due to an unknown error'
                return createErrorToast(
                    `Something went wrong. Your import failed ${message}. Please check the CSV file for errors, refresh the page and try again`,
                    { timeout: 0 }
                )
            },
        },
    })
}

export const startForecasting = (id, data) => {
    return {
        type: actionTypes.START_FORECASTING,
        id,
        data,
    }
}

export default runbooks
